import * as Sentry from "@sentry/react";
import { sentryConfig } from "./config/sentry";
import {
  parseReferenceId,
  resolveTenantFromBackendUrl,
  scrubSentryEvent,
  shouldCaptureApiStatus,
  truncateResponseBody,
} from "./sentryCapture";

let initialized = false;

export type SentryBootstrapContext = {
  backendURL?: string | null;
  websiteId?: string | number | null;
  websiteUuid?: string | null;
};

export function initSentry(): void {
  if (initialized || !sentryConfig.enabled || !sentryConfig.dsn) {
    return;
  }

  Sentry.init({
    dsn: sentryConfig.dsn,
    attachStacktrace: true,
    tracesSampleRate: sentryConfig.tracesSampleRate,
    ignoreErrors: sentryConfig.ignoreErrors,
    beforeSend(event) {
      return scrubSentryEvent(event);
    },
  });

  initialized = true;
}

export function configureSentryContext(context: SentryBootstrapContext): void {
  if (!initialized) {
    return;
  }

  const tenant = resolveTenantFromBackendUrl(context.backendURL);
  if (tenant) {
    Sentry.setTag("tenant", tenant);
  }

  if (context.backendURL) {
    Sentry.setTag("backend_url", String(context.backendURL));
  }

  if (context.websiteId != null && context.websiteId !== "") {
    Sentry.setTag("website_id", String(context.websiteId));
  }

  if (context.websiteUuid) {
    Sentry.setTag("website_uuid", String(context.websiteUuid));
  }

  Sentry.setTag("source", "erpweb-builder");
}

export function setSentryWebsiteName(name: string | null | undefined): void {
  if (!initialized || !name) {
    return;
  }

  Sentry.setTag("website_name", String(name));
}

export function captureFetchApiFailure(
  status: number,
  url: string,
  body: unknown
): void {
  if (!initialized || !shouldCaptureApiStatus(status)) {
    return;
  }

  const referenceId = parseReferenceId(body);
  const responseBody =
    typeof body === "string"
      ? truncateResponseBody(body)
      : truncateResponseBody(JSON.stringify(body ?? ""));

  Sentry.withScope((scope) => {
    scope.setTag("http_status", String(status));
    scope.setTag("error_source", "fetch");
    scope.setExtra("request_url", url);
    scope.setExtra("response_body", responseBody);

    if (referenceId) {
      scope.setTag("reference_id", referenceId);
    }

    Sentry.captureMessage(`Fetch request failed with status ${status}`, "error");
  });
}

export function captureReactError(
  error: Error,
  componentStack?: string | null
): void {
  if (!initialized) {
    return;
  }

  Sentry.captureException(error, {
    extra: componentStack ? { componentStack } : undefined,
  });
}

export { Sentry };
